import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Field } from "../FormField";
import { useAuth } from "../../AuthContext";
import { GripVertical, Plus, Trash2, FileText, Video, Image as ImageIcon, Type, Link2, Upload, Loader2, AlertTriangle, ExternalLink, ConciergeBell, Languages, Check, Send, Undo2, Pencil } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 5 * 1024 * 1024; // 5MB
const TRANS_LANGS = [["en", "EN"], ["es", "ES"]];
const PUBLISHER_ROLES = ["super_admin", "amministratore", "admin"];
const STATUS_META = {
  non_tradotto: { label: "Non tradotto", cls: "bg-slate-100 text-slate-500" },
  generata: { label: "Generata", cls: "bg-sky-50 text-sky-600" },
  da_verificare: { label: "Da verificare", cls: "bg-amber-50 text-amber-600" },
  approvato: { label: "Approvato", cls: "bg-indigo-50 text-indigo-600" },
  pubblicato: { label: "Pubblicato", cls: "bg-emerald-50 text-emerald-600" },
};

const TYPE_META = {
  text: { label: "Testo", icon: Type, accept: "" },
  pdf: { label: "PDF", icon: FileText, accept: "application/pdf" },
  video: { label: "Video", icon: Video, accept: "video/mp4,video/quicktime,video/webm" },
  image: { label: "Immagine", icon: ImageIcon, accept: "image/jpeg,image/png,image/webp" },
  link: { label: "Link esterno", icon: Link2, accept: "" },
};
const FILE_TYPES = ["pdf", "video", "image"];

export default function WelcomeCenterManager({ aptId }) {
  const { user } = useAuth();
  const isPublisher = PUBLISHER_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [savingSecs, setSavingSecs] = useState(false);
  const [dialog, setDialog] = useState(null); // { category, form }
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busyTrans, setBusyTrans] = useState({}); // rid|rid:lang -> true
  const [transEdit, setTransEdit] = useState(null); // { rid, lang, title, body }
  const fileRef = useRef();
  const dragIdx = useRef(null);

  const load = useCallback(() => {
    if (!aptId) return;
    api.get(`/welcome-center/${aptId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [aptId]);
  useEffect(() => { load(); }, [load]);

  const sections = data?.sections || [];
  const resources = data?.resources || [];
  const resByCat = (key) => resources.filter((r) => r.category === key).sort((a, b) => (a.order || 0) - (b.order || 0));

  const persistSections = async (secs) => {
    setSavingSecs(true);
    try {
      const r = await api.put(`/welcome-center/${aptId}/sections`, { sections: secs.map((s, i) => ({ key: s.key, enabled: s.enabled, order: i })) });
      setData((d) => ({ ...d, sections: r.data.sections }));
    } catch { toast.error("Errore salvataggio sezioni"); load(); }
    finally { setSavingSecs(false); }
  };

  const toggleSection = (key, enabled) => {
    const next = sections.map((s) => s.key === key ? { ...s, enabled } : s);
    setData((d) => ({ ...d, sections: next }));
    persistSections(next);
  };

  const onDragStart = (i) => { dragIdx.current = i; };
  const onDrop = (i) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from === null || from === i) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setData((d) => ({ ...d, sections: next }));
    persistSections(next);
  };

  const openAdd = (category) => setDialog({ category, form: { type: "text", title: "", body: "", url: "" }, file: null });

  const chunkedUpload = async (file) => {
    const init = await api.post(`/welcome-center/upload/init`, { filename: file.name, total_size: file.size });
    const { upload_id, ext, content_type } = init.data;
    const parts = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < parts; i++) {
      const fd = new FormData();
      fd.append("index", i);
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await api.post(`/welcome-center/upload/${upload_id}/part`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgress(Math.round(((i + 1) / parts) * 100));
    }
    const done = await api.post(`/welcome-center/upload/${upload_id}/complete`, { ext, content_type, filename: file.name });
    return done.data; // {url, storage_path, content_type, file_name}
  };

  const saveResource = async () => {
    const { category, form, file } = dialog;
    if (!form.title.trim()) return toast.error("Inserisci un titolo");
    const payload = { category, type: form.type, title: form.title.trim(), body: form.body, url: form.url, order: resByCat(category).length };
    try {
      if (FILE_TYPES.includes(form.type)) {
        if (!file) return toast.error("Seleziona un file");
        setUploading(true); setProgress(0);
        const up = await chunkedUpload(file);
        payload.url = up.url; payload.storage_path = up.storage_path;
        payload.content_type = up.content_type; payload.file_name = up.file_name;
      } else if (form.type === "link" && !form.url.trim()) {
        return toast.error("Inserisci un URL");
      }
      await api.post(`/welcome-center/${aptId}/resource`, payload);
      toast.success("Contenuto aggiunto");
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setUploading(false); setProgress(0); }
  };

  const delResource = async (rid) => {
    if (!window.confirm("Eliminare questo contenuto?")) return;
    try { await api.delete(`/welcome-center/${aptId}/resource/${rid}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const isTranslatable = (r) => !!((r.title || "").trim() || (r.body || "").trim());

  const translateResource = async (rid) => {
    setBusyTrans((b) => ({ ...b, [rid]: true }));
    try { await api.post(`/welcome-center/${aptId}/resource/${rid}/translate`, { langs: ["en", "es"] }); toast.success("Traduzioni generate (EN, ES)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore traduzione"); }
    finally { setBusyTrans((b) => ({ ...b, [rid]: false })); }
  };

  const translateAll = async () => {
    setBusyTrans((b) => ({ ...b, ALL: true }));
    try { const r = await api.post(`/welcome-center/${aptId}/translate-all`, { langs: ["en", "es"] }); toast.success(`Ivy ha generato ${r.data.generated} traduzioni`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyTrans((b) => ({ ...b, ALL: false })); }
  };

  const setTransStatus = async (rid, lang, status) => {
    const key = `${rid}:${lang}`;
    setBusyTrans((b) => ({ ...b, [key]: true }));
    try { await api.post(`/welcome-center/${aptId}/resource/${rid}/translation/${lang}/status`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyTrans((b) => ({ ...b, [key]: false })); }
  };

  const saveTransEdit = async () => {
    const { rid, lang, title, body } = transEdit;
    try { await api.put(`/welcome-center/${aptId}/resource/${rid}/translation/${lang}`, { title, body }); toast.success("Traduzione aggiornata (Da verificare)"); setTransEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-400" data-testid="wc-loading">…</div>;

  return (
    <Card className="border-slate-200 p-5" data-testid="welcome-center-manager">
      <div className="mb-1 flex items-center gap-2">
        <ConciergeBell size={18} className="text-primary" />
        <h3 className="font-head font-semibold text-slate-900">Guest Welcome Center</h3>
        {savingSecs && <Loader2 size={13} className="animate-spin text-slate-400" />}
        <Button size="sm" variant="outline" className="ml-auto h-8 gap-1.5 text-xs" data-testid="wc-translate-all" disabled={!!busyTrans.ALL} onClick={translateAll}>
          {busyTrans.ALL ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />} Traduci tutto (EN+ES) con Ivy
        </Button>
      </div>
      <p className="mb-4 text-xs text-slate-400">Ospita PDF, video, immagini e testi direttamente su Urban OS (fino a {Math.round((data.max_bytes || 0) / (1024 * 1024))}MB). Attiva le sezioni utili per questo appartamento e trascina per riordinarle: l'ospite vedrà solo quelle attive con contenuti. Le traduzioni sono visibili all'ospite solo dopo la <b>pubblicazione</b>.</p>

      <div className="space-y-2" data-testid="wc-sections">
        {sections.map((s, i) => {
          const items = resByCat(s.key);
          const incomplete = s.enabled && items.length === 0;
          return (
            <div key={s.key} data-testid={`wc-section-${s.key}`} draggable
              onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              className={`rounded-lg border ${s.enabled ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center gap-3 px-3 py-2.5">
                <GripVertical size={16} className="shrink-0 cursor-move text-slate-300" />
                <span className="text-lg">{s.emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-800">{s.label}</span>
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{items.length}</span>
                    {incomplete && <span className="flex items-center gap-1 rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-600" data-testid={`wc-incomplete-${s.key}`}><AlertTriangle size={10} /> da completare</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`wc-add-${s.key}`} onClick={() => openAdd(s.key)}><Plus size={13} /> Contenuto</Button>
                <Switch checked={s.enabled} onCheckedChange={(v) => toggleSection(s.key, v)} data-testid={`wc-toggle-${s.key}`} />
              </div>
              {items.length > 0 && (
                <div className="space-y-1 border-t border-slate-100 px-3 py-2">
                  {items.map((r) => {
                    const Icon = (TYPE_META[r.type] || TYPE_META.text).icon;
                    const trans = r.translations || {};
                    return (
                      <div key={r.id} className="rounded-md border border-slate-100 px-2 py-1.5" data-testid={`wc-resource-${r.id}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <Icon size={14} className="shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate text-slate-700">{r.title || r.file_name || "(senza titolo)"}</span>
                          {(r.url && r.type !== "text") && <a href={r.type === "link" ? r.url : `${BACKEND}${r.url}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-primary"><ExternalLink size={13} /></a>}
                          <button className="text-slate-400 hover:text-red-600" data-testid={`wc-del-${r.id}`} onClick={() => delResource(r.id)}><Trash2 size={13} /></button>
                        </div>
                        {isTranslatable(r) && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-slate-50 pt-1.5" data-testid={`wc-trans-${r.id}`}>
                            <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-slate-400"><Languages size={11} /> Traduzioni</span>
                            {TRANS_LANGS.map(([lg, lbl]) => {
                              const tr = trans[lg];
                              const st = tr?.status || "non_tradotto";
                              const meta = STATUS_META[st];
                              const key = `${r.id}:${lg}`;
                              const busy = busyTrans[r.id] || busyTrans[key];
                              return (
                                <div key={lg} className="flex items-center gap-1 rounded-md bg-slate-50 px-1.5 py-1" data-testid={`wc-trans-${r.id}-${lg}`}>
                                  <span className="text-[10px] font-bold text-slate-500">{lbl}</span>
                                  <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${meta.cls}`} data-testid={`wc-trans-status-${r.id}-${lg}`}>{meta.label}</span>
                                  {busy && <Loader2 size={11} className="animate-spin text-slate-400" />}
                                  {!busy && st === "non_tradotto" && (
                                    <button title="Genera con Ivy" data-testid={`wc-trans-gen-${r.id}-${lg}`} className="text-sky-600 hover:text-sky-800" onClick={() => translateResource(r.id)}><Languages size={12} /></button>
                                  )}
                                  {!busy && st !== "non_tradotto" && (
                                    <>
                                      <button title="Modifica" data-testid={`wc-trans-edit-${r.id}-${lg}`} className="text-slate-400 hover:text-slate-700" onClick={() => setTransEdit({ rid: r.id, lang: lg, title: tr?.title || "", body: tr?.body || "" })}><Pencil size={11} /></button>
                                      {isPublisher && (st === "generata" || st === "da_verificare") && (
                                        <button title="Approva" data-testid={`wc-trans-approve-${r.id}-${lg}`} className="text-indigo-600 hover:text-indigo-800" onClick={() => setTransStatus(r.id, lg, "approvato")}><Check size={12} /></button>
                                      )}
                                      {isPublisher && st === "approvato" && (
                                        <button title="Pubblica" data-testid={`wc-trans-publish-${r.id}-${lg}`} className="text-emerald-600 hover:text-emerald-800" onClick={() => setTransStatus(r.id, lg, "pubblicato")}><Send size={11} /></button>
                                      )}
                                      {isPublisher && st === "pubblicato" && (
                                        <button title="Ritira" data-testid={`wc-trans-unpublish-${r.id}-${lg}`} className="text-amber-600 hover:text-amber-800" onClick={() => setTransStatus(r.id, lg, "da_verificare")}><Undo2 size={11} /></button>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && !uploading && setDialog(null)}>
        <DialogContent data-testid="wc-add-dialog">
          <DialogHeader><DialogTitle>Nuovo contenuto · {sections.find((s) => s.key === dialog?.category)?.label}</DialogTitle>
            <DialogDescription>Il contenuto sarà mostrato all'ospite nella sezione selezionata (se attiva).</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <Field label="Tipo di contenuto">
                <Select value={dialog.form.type} onValueChange={(v) => setDialog((d) => ({ ...d, form: { ...d.form, type: v }, file: null }))}>
                  <SelectTrigger data-testid="wc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Titolo"><Input data-testid="wc-title-input" value={dialog.form.title} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, title: e.target.value } }))} placeholder="Es. Come raggiungere il parcheggio" /></Field>
              {dialog.form.type === "text" && (
                <Field label="Testo (editabile)"><Textarea data-testid="wc-body-input" rows={5} value={dialog.form.body} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, body: e.target.value } }))} placeholder="Scrivi le informazioni per l'ospite…" /></Field>
              )}
              {dialog.form.type === "link" && (
                <Field label="URL esterno"><Input data-testid="wc-url-input" value={dialog.form.url} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, url: e.target.value } }))} placeholder="https://…" /></Field>
              )}
              {FILE_TYPES.includes(dialog.form.type) && (
                <div>
                  <input ref={fileRef} type="file" className="hidden" accept={TYPE_META[dialog.form.type].accept}
                    onChange={(e) => setDialog((d) => ({ ...d, file: e.target.files?.[0] || null }))} data-testid="wc-file-input" />
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()}>
                    <Upload size={14} /> {dialog.file ? dialog.file.name : "Seleziona file"}
                  </Button>
                  {dialog.file && <span className="ml-2 text-xs text-slate-400">{(dialog.file.size / (1024 * 1024)).toFixed(1)} MB</span>}
                </div>
              )}
              {uploading && <div><Progress value={progress} className="h-2" /><p className="mt-1 text-xs text-slate-400">Caricamento… {progress}%</p></div>}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" disabled={uploading} onClick={() => setDialog(null)}>Annulla</Button>
                <Button className="bg-primary hover:bg-primary/90" data-testid="wc-save-resource" disabled={uploading} onClick={saveResource}>
                  {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Salva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!transEdit} onOpenChange={(o) => !o && setTransEdit(null)}>
        <DialogContent data-testid="wc-trans-edit-dialog">
          <DialogHeader><DialogTitle>Modifica traduzione · {transEdit?.lang?.toUpperCase()}</DialogTitle>
            <DialogDescription>La modifica riporta la traduzione in stato "Da verificare" e non altera le altre lingue.</DialogDescription>
          </DialogHeader>
          {transEdit && (
            <div className="space-y-3">
              <Field label="Titolo"><Input data-testid="wc-trans-edit-title" value={transEdit.title} onChange={(e) => setTransEdit((t) => ({ ...t, title: e.target.value }))} /></Field>
              <Field label="Testo"><Textarea data-testid="wc-trans-edit-body" rows={5} value={transEdit.body} onChange={(e) => setTransEdit((t) => ({ ...t, body: e.target.value }))} /></Field>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTransEdit(null)}>Annulla</Button>
                <Button className="bg-primary hover:bg-primary/90" data-testid="wc-trans-edit-save" onClick={saveTransEdit}>Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
